import { useMutation } from "hooks/useQueries";
import { capitalize, isEmpty } from "lodash";
import { useEffect, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import axiosInstance from "utils/axiosInstance";
import { useInvestorContext } from "./InvestorContext";

interface KraData {
  name: string;
  address: string;
  gender: string;
  pincode: string;
  country?: string;
  state?: string;
  city?: string;
}

interface KRAApiResponse {
  data: KraData;
}

/**
 * Handles fetching KRA data and sequentially setting
 * country → state → city safely as dependent lists load.
 */
export const useKRADataHandler = (
  form: UseFormReturn<any>,
  basketId: string,
) => {
  const {
    state: { countryList, stateList, cityList },
    dispatch,
  } = useInvestorContext();

  // Persist KRA data across effects while lists load
  const kraDataRef = useRef<KraData | null>(null);

  const { mutate: handleGetKRAData, isLoading: KRALoading } = useMutation({
    mutationKey: ["GET_KRA_DATA"],

    mutationFn: (payload: Record<string, any>) =>
      axiosInstance.post<KRAApiResponse>(
        `/pan-details/?basket_id=${basketId}`,
        payload,
        {
          headers: { Authorization: null },
        },
      ),

    onSuccess: (response) => {
      const data = response?.data?.data;
      if (!data) return;

      kraDataRef.current = data;

      // ✅ Basic personal details
      form.setValue("clientCreation.personalDetails.name", data.name || "");
      form.setValue(
        "clientCreation.personalDetails.address",
        data.address || "",
      );
      form.setValue("clientCreation.personalDetails.gender", data.gender || "");
      form.setValue(
        "clientCreation.personalDetails.pincode",
        data.pincode || "",
      );
      form.setValue("fetchedFromKra", true);

      // ✅ Handle Country
      if (!isEmpty(data.country) && countryList?.length) {
        const matchedCountry = countryList.find(
          (item: any) =>
            item.name.trim().toLowerCase() ===
            data.country.trim().toLowerCase(),
        );

        if (matchedCountry) {
          dispatch({ type: "SELECTED_COUNTRY", data: matchedCountry.id });
          form.setValue(
            "clientCreation.personalDetails.country",
            capitalize(data.country),
          );
        }
      }
    },
  });

  // ✅ Handle State after stateList loads
  useEffect(() => {
    const data = kraDataRef.current;
    if (!data?.state || !stateList?.length) return;

    const matchedState = stateList.find(
      (item: any) =>
        item.name.trim().toLowerCase() === data.state.trim().toLowerCase(),
    );

    if (matchedState) {
      dispatch({ type: "SELECTED_STATE", data: matchedState.id });
      form.setValue(
        "clientCreation.personalDetails.state",
        capitalize(data.state),
      );
    }
  }, [stateList]);

  // ✅ Handle City after cityList loads
  useEffect(() => {
    const data = kraDataRef.current;
    if (!data?.city || !cityList?.length) return;

    const matchedCity = cityList.find(
      (item: any) =>
        item.name.trim().toLowerCase() === data.city.trim().toLowerCase(),
    );

    if (matchedCity) {
      dispatch({ type: "SELECTED_CITY", data: matchedCity.id });
      form.setValue(
        "clientCreation.personalDetails.city",
        capitalize(data.city),
      );
    }
  }, [cityList]);

  return { handleGetKRAData, KRALoading };
};
